/**
 * Paints: a solid colour or a gradient, wherever a colour can be picked.
 *
 * A gradient is stored in a colour field as a CSS gradient string, e.g.
 * `linear-gradient(180deg, #ffffff 0%, #7c3aed 100%)` or
 * `radial-gradient(circle, #ffffff 0%, #000000 100%)`, so string colour
 * fields (a colour clip's fill, the project background, shape colours…)
 * take one without a schema change, and CSS shows it as is. Renderers draw
 * it with canvasPaint() over the box being painted.
 */
export interface GradientStop {
  /** Position along the gradient, 0..1. */
  at: number;
  color: string;
}

export interface Gradient {
  type: 'linear' | 'radial';
  /** Linear gradients: direction in degrees (0 = left → right, 90 = top → bottom). */
  angle: number;
  stops: GradientStop[];
}

export const MAX_GRADIENT_STOPS = 8;

/** A solid colour: #rgb, #rrggbb (with or without alpha) or rgb()/rgba(). */
export const SOLID_COLOR = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+%?\s*)?\))$/;

export const isSolidColor = (v: unknown): v is string => typeof v === 'string' && v.length <= 40 && SOLID_COLOR.test(v.trim());

const num = (v: unknown, lo: number, hi: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;

/** A gradient object with valid values only (stops sorted); undefined when it has fewer than two stops. */
export function sanitizeGradient(raw: unknown): Gradient | undefined {
  const g = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!g || !Array.isArray(g.stops)) return undefined;
  const stops = (g.stops as unknown[])
    .slice(0, MAX_GRADIENT_STOPS)
    .flatMap((s) => {
      const o = s && typeof s === 'object' ? (s as Record<string, unknown>) : null;
      return o ? [{ at: num(o.at, 0, 1, 0), color: isSolidColor(o.color) ? o.color.trim().toLowerCase() : '#ffffff' }] : [];
    })
    .sort((a, b) => a.at - b.at);
  if (stops.length < 2) return undefined;
  return { type: g.type === 'radial' ? 'radial' : 'linear', angle: num(g.angle, -360, 360, 90), stops };
}

/** Splits on commas outside parentheses. */
function splitTop(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of s) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const SIDES: Record<string, number> = {
  'to right': 0,
  'to bottom right': 45,
  'to right bottom': 45,
  'to bottom': 90,
  'to bottom left': 135,
  'to left bottom': 135,
  'to left': 180,
  'to top left': -135,
  'to left top': -135,
  'to top': -90,
  'to top right': -45,
  'to right top': -45,
};

/** The gradient a CSS linear-/radial-gradient string describes, or null for anything else (solid colours included). */
export function parseGradient(value: unknown): Gradient | null {
  if (typeof value !== 'string' || value.length > 600) return null;
  const m = /^\s*(linear|radial)-gradient\((.*)\)\s*$/i.exec(value);
  if (!m) return null;
  const type = m[1].toLowerCase() as Gradient['type'];
  const parts = splitTop(m[2]);
  // CSS angles run clockwise from "to the top"; ours from "to the right".
  let angle = 90;
  const first = parts[0]?.toLowerCase() ?? '';
  const deg = /^(-?[\d.]+)(deg|turn|rad)?$/.exec(first);
  if (type === 'linear' && deg) {
    const n = parseFloat(deg[1]);
    const d = deg[2] === 'turn' ? n * 360 : deg[2] === 'rad' ? (n * 180) / Math.PI : n;
    angle = d - 90;
    parts.shift();
  } else if (type === 'linear' && first in SIDES) {
    angle = SIDES[first];
    parts.shift();
  } else if (type === 'radial' && first && !isSolidColor(first.split(/\s+/)[0])) {
    // Shape / size / position: drawn as a centred circle.
    parts.shift();
  }
  if (!Number.isFinite(angle)) return null;
  const raw: { color: string; at: number | null }[] = [];
  for (const p of parts.slice(0, MAX_GRADIENT_STOPS)) {
    const s = /^(.*?)(?:\s+(-?[\d.]+)%)?$/.exec(p.trim());
    if (!s || !isSolidColor(s[1])) return null;
    raw.push({ color: s[1].trim().toLowerCase(), at: s[2] !== undefined ? Math.min(1, Math.max(0, Number((parseFloat(s[2]) / 100).toFixed(4)))) : null });
  }
  if (raw.length < 2) return null;
  // Missing positions: the ends at 0 and 1, the rest spread evenly between the known ones (as CSS does).
  if (raw[0].at === null) raw[0].at = 0;
  if (raw[raw.length - 1].at === null) raw[raw.length - 1].at = 1;
  for (let i = 1; i < raw.length; i++) {
    if (raw[i].at !== null) continue;
    let j = i;
    while (raw[j].at === null) j++;
    const a = raw[i - 1].at!;
    const b = raw[j].at!;
    for (let k = i; k < j; k++) raw[k].at = a + ((b - a) * (k - i + 1)) / (j - i + 1);
  }
  let last = 0;
  const stops = raw.map((r) => {
    last = Math.max(last, r.at!);
    return { at: last, color: r.color };
  });
  return { type, angle: ((((angle + 180) % 360) + 360) % 360) - 180, stops };
}

const round = (n: number, digits: number) => Number(n.toFixed(digits));

/** The CSS string of a gradient (what colour fields store). */
export function gradientCss(g: Gradient): string {
  const stops = g.stops.map((s) => `${s.color} ${round(s.at * 100, 1)}%`).join(', ');
  return g.type === 'radial' ? `radial-gradient(circle, ${stops})` : `linear-gradient(${round((((g.angle + 90) % 360) + 360) % 360, 1)}deg, ${stops})`;
}

export const isGradient = (v: unknown): boolean => parseGradient(v) !== null;

/** A colour field's value tidied: a solid colour (trimmed, lowercase) or a gradient's canonical CSS; null when it is neither. */
export function sanitizePaint(v: unknown): string | null {
  if (isSolidColor(v)) return v.trim().toLowerCase();
  const g = parseGradient(v);
  return g ? gradientCss(g) : null;
}

/** One colour standing in for a paint where only a solid colour works (a gradient's first stop). */
export function solidOf(v: string, fallback = '#000000'): string {
  if (isSolidColor(v)) return v.trim();
  return parseGradient(v)?.stops[0].color ?? fallback;
}

/** A two-stop gradient starting at `color`, for when a field switches to a gradient. */
export function gradientFrom(color: string): Gradient {
  const c = isSolidColor(color) ? color.trim().toLowerCase() : '#ffffff';
  const rgb = parseRgb(c);
  // The second stop is the colour darkened, or lightened when it is already dark.
  const dark = rgb ? rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114 < 80 : false;
  const end = rgb ? toHex(rgb.map((x) => (dark ? x + (255 - x) * 0.6 : x * 0.4)) as [number, number, number]) : '#000000';
  return { type: 'linear', angle: 90, stops: [{ at: 0, color: c }, { at: 1, color: end }] };
}

/** #rgb, #rrggbb(aa) or rgb()/rgba() as 0..255 channels (alpha dropped). */
export function parseRgb(c: string): [number, number, number] | null {
  const v = c.trim().toLowerCase();
  let m = /^#([0-9a-f]{3,4})$/.exec(v);
  if (m) return [0, 1, 2].map((i) => parseInt(m![1][i] + m![1][i], 16)) as [number, number, number];
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/.exec(v);
  if (m) return [0, 2, 4].map((i) => parseInt(m![1].slice(i, i + 2), 16)) as [number, number, number];
  m = /^rgba?\(\s*([\d.]+)(%?)\s*,\s*([\d.]+)(%?)\s*,\s*([\d.]+)(%?)/.exec(v);
  if (m) {
    const ch = (n: string, pct: string) => Math.min(255, Math.max(0, pct ? (parseFloat(n) * 255) / 100 : parseFloat(n)));
    return [ch(m[1], m[2]), ch(m[3], m[4]), ch(m[5], m[6])];
  }
  return null;
}

const toHex = (rgb: [number, number, number]) => `#${rgb.map((x) => Math.round(Math.min(255, Math.max(0, x))).toString(16).padStart(2, '0')).join('')}`;

/** The gradient's colour at position `t` (0..1) as #rrggbb. */
export function sampleGradient(g: Gradient, t: number): string {
  const s = g.stops;
  let i = 0;
  while (i < s.length - 1 && s[i + 1].at < t) i++;
  const a = s[i];
  const b = s[Math.min(i + 1, s.length - 1)];
  const u = b.at > a.at ? Math.min(1, Math.max(0, (t - a.at) / (b.at - a.at))) : t >= b.at ? 1 : 0;
  const ca = parseRgb(a.color) ?? [255, 255, 255];
  const cb = parseRgb(b.color) ?? ca;
  return toHex([0, 1, 2].map((k) => ca[k] + (cb[k] - ca[k]) * u) as [number, number, number]);
}

/** Every stop of a gradient recoloured by `fn` (e.g. darkened). */
export function mapGradient(g: Gradient, fn: (color: string) => string): Gradient {
  return { ...g, stops: g.stops.map((s) => ({ ...s, color: fn(s.color) })) };
}

export interface GradientCanvas {
  createLinearGradient(x0: number, y0: number, x1: number, y1: number): { addColorStop(offset: number, color: string): void };
  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number): { addColorStop(offset: number, color: string): void };
}

/**
 * A fill/stroke style for a box: the canvas gradient spanning the box for a
 * gradient (object or CSS string), else the colour itself.
 */
export function canvasPaint(ctx: GradientCanvas, value: Gradient | string | undefined | null, x: number, y: number, w: number, h: number): unknown {
  const g = typeof value === 'string' ? parseGradient(value) : value;
  if (!g) return typeof value === 'string' ? value : '#000000';
  const cx = x + w / 2;
  const cy = y + h / 2;
  let grad: { addColorStop(offset: number, color: string): void };
  if (g.type === 'radial') grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(1, Math.hypot(w, h) / 2));
  else {
    const a = (g.angle * Math.PI) / 180;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    // Half the length of the box seen along the direction: the gradient spans it end to end.
    const l = Math.max(1, Math.abs((w / 2) * dx) + Math.abs((h / 2) * dy));
    grad = ctx.createLinearGradient(cx - dx * l, cy - dy * l, cx + dx * l, cy + dy * l);
  }
  for (const st of g.stops) grad.addColorStop(st.at, st.color);
  return grad;
}

/**
 * A colour clip's fill or a project background: '#rrggbb' (lowercase) or a
 * gradient's canonical CSS; null for anything else.
 */
export function sanitizeFill(v: unknown): string | null {
  if (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim())) return v.trim().toLowerCase();
  const g = parseGradient(v);
  return g ? gradientCss(g) : null;
}
